import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FileText,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  DollarSign,
  PieChart,
} from 'lucide-react';
import { GlassCard, GlassButton, GlassChart, GlassInput, FadeIn } from '../../components/ui';
import { api } from '../../hooks/useApi';

const today = new Date();
const startOfYear = `${today.getFullYear()}-01-01`;
const todayIso = today.toISOString().slice(0, 10);

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

const formatPercent = (value) => `${Number(value || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;

const DRELine = ({ label, value, isTotal = false, isSubtotal = false, indent = false }) => (
  <div className={`flex justify-between items-center py-2 ${isTotal ? 'border-t border-white/20' : ''} ${indent ? 'pl-6' : ''}`}>
    <span className={`${isTotal ? 'font-semibold text-white' : isSubtotal ? 'text-white/80' : 'text-white/60'}`}>
      {label}
    </span>
    <span className={`${isTotal ? 'font-bold text-lg' : ''} ${value >= 0 ? 'text-green-400' : 'text-red-400'}`}>
      {formatCurrency(value)}
    </span>
  </div>
);

export const FinanceiroDRE = () => {
  const [period, setPeriod] = useState({ inicio: startOfYear, fim: todayIso });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadDre = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/contabilidade/dre', {
        params: {
          data_inicio: period.inicio,
          data_fim: period.fim,
        },
      });
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao carregar DRE.');
    } finally {
      setLoading(false);
    }
  }, [period.fim, period.inicio]);

  useEffect(() => {
    loadDre();
  }, [loadDre]);

  const linhas = data?.linhas || {};
  const receitas = data?.receitas || 0;
  const despesas = data?.despesas || 0;
  const resultado = data?.resultado || 0;
  const margem = data?.margem || 0;

  const cards = useMemo(() => {
    const despesaRatio = receitas > 0 ? (despesas / receitas) * 100 : 0;
    return {
      margem,
      despesaRatio,
      resultado,
    };
  }, [despesas, margem, receitas, resultado]);

  const books = data?.books || [];
  const expenseDistribution = data?.expense_distribution || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white/90">Demonstracao de Resultado</h1>
          <p className="text-white/50">
            {loading ? 'Carregando DRE real...' : `Periodo ${period.inicio} a ${period.fim}`}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <GlassInput
            type="date"
            value={period.inicio}
            onChange={(e) => setPeriod((current) => ({ ...current, inicio: e.target.value }))}
            className="min-w-[150px]"
          />
          <GlassInput
            type="date"
            value={period.fim}
            onChange={(e) => setPeriod((current) => ({ ...current, fim: e.target.value }))}
            className="min-w-[150px]"
          />
          <GlassButton variant="secondary" icon={RefreshCw} onClick={loadDre} loading={loading}>
            Atualizar
          </GlassButton>
        </div>
      </div>

      {error && (
        <GlassCard className="p-4 border-red-500/30 bg-red-500/10" hover={false}>
          <p className="text-sm text-red-300">{error}</p>
        </GlassCard>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <GlassCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white/50">Margem</p>
              <p className={`text-3xl font-bold ${cards.margem >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {formatPercent(cards.margem)}
              </p>
            </div>
            <div className="p-3 rounded-xl bg-green-500/20">
              <TrendingUp className="w-6 h-6 text-green-400" />
            </div>
          </div>
        </GlassCard>
        <GlassCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white/50">Despesas / Receita</p>
              <p className="text-3xl font-bold text-accent-primary">{formatPercent(cards.despesaRatio)}</p>
            </div>
            <div className="p-3 rounded-xl bg-accent-primary/20">
              <DollarSign className="w-6 h-6 text-accent-primary" />
            </div>
          </div>
        </GlassCard>
        <GlassCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white/50">Resultado</p>
              <p className={`text-3xl font-bold ${cards.resultado >= 0 ? 'text-purple-400' : 'text-red-400'}`}>
                {formatCurrency(cards.resultado)}
              </p>
            </div>
            <div className="p-3 rounded-xl bg-purple-500/20">
              <PieChart className="w-6 h-6 text-purple-400" />
            </div>
          </div>
        </GlassCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <FadeIn>
          <GlassCard className="h-full">
            <div className="p-6 border-b border-white/10">
              <h3 className="text-lg font-semibold text-white/90">Demonstracao do Resultado</h3>
              <p className="text-sm text-white/50">Valores calculados a partir de movimentacoes pagas</p>
            </div>
            <div className="p-6">
              <DRELine label="Receita Bruta" value={linhas.receita_bruta || receitas} />
              <DRELine label="Receita Liquida" value={linhas.receita_liquida || receitas} isSubtotal />

              <div className="h-px bg-white/10 my-2" />

              <DRELine label="(-) Custos e Despesas" value={-(linhas.custos_despesas || despesas)} indent />
              <DRELine label="Resultado Operacional" value={linhas.resultado_operacional || resultado} isSubtotal />

              <div className="h-px bg-white/10 my-2" />

              <DRELine label="RESULTADO LIQUIDO" value={linhas.resultado_liquido || resultado} isTotal />
            </div>
          </GlassCard>
        </FadeIn>

        <div className="space-y-6">
          <FadeIn delay={0.1}>
            <GlassCard>
              <div className="p-6 border-b border-white/10">
                <h3 className="text-lg font-semibold text-white/90">Performance por Book</h3>
              </div>
              <div className="p-6">
                <GlassChart
                  type="bar"
                  data={books}
                  dataKeys={[
                    { key: 'receita', name: 'Receita' },
                    { key: 'resultado', name: 'Resultado' },
                  ]}
                  xAxisKey="name"
                  height={220}
                  colors={['#6366f1', '#22c55e']}
                />
              </div>
            </GlassCard>
          </FadeIn>

          <FadeIn delay={0.2}>
            <GlassCard>
              <div className="p-6 border-b border-white/10">
                <h3 className="text-lg font-semibold text-white/90">Distribuicao de Despesas</h3>
              </div>
              <div className="p-6">
                {expenseDistribution.length > 0 ? (
                  <GlassChart
                    type="pie"
                    data={expenseDistribution}
                    dataKeys={['value']}
                    height={220}
                  />
                ) : (
                  <div className="h-[220px] flex items-center justify-center text-white/50">
                    <div className="text-center">
                      <FileText className="w-8 h-8 mx-auto mb-3 text-white/30" />
                      <p>Nenhuma despesa paga no periodo.</p>
                    </div>
                  </div>
                )}
              </div>
            </GlassCard>
          </FadeIn>
        </div>
      </div>
    </div>
  );
};

export default FinanceiroDRE;
